// src/game/moves.ts
// Undo / reset history (§2.5, §7). Immutable snapshot stack; moveCount lives here, not in state.

import type { GameState } from './rules';

/**
 * Immutable snapshot stack. The payoff of immutable state: undo = pop, reset = jump-to-initial,
 * un-exiting a removed block is automatic (the prior snapshot still contains it).
 *   `initial` — generated starting state, retained for Reset.
 *   `past`    — snapshots strictly before `present`, oldest -> newest.
 *   `present` — current state.
 */
export interface History {
  readonly initial: GameState;
  readonly past: readonly GameState[];
  readonly present: GameState;
}

export function initHistory(initial: GameState): History {
  return { initial, past: [], present: initial };
}

/**
 * Push `present` onto `past`, set `next` as the new present. Caller MUST only commit a NON-NOOP
 * transition (result.kind !== 'noop'); committing a no-op is a contract violation.
 */
export function commit(h: History, next: GameState): History {
  return { initial: h.initial, past: [...h.past, h.present], present: next };
}

/** Pop one snapshot (present <- last of past). No-op if !canUndo. */
export function undo(h: History): History {
  if (h.past.length === 0) {
    return h;
  }
  const present = h.past[h.past.length - 1]!; // guarded by length check
  const past = h.past.slice(0, -1);
  return { initial: h.initial, past, present };
}

/** Back to `initial`; clears `past`. */
export function reset(h: History): History {
  return { initial: h.initial, past: [], present: h.initial };
}

/** True iff past.length > 0. */
export function canUndo(h: History): boolean {
  return h.past.length > 0;
}

/** Recorded (non-noop) move count, === past.length. Backs the cosmetic par display (§2.6). */
export function moveCount(h: History): number {
  return h.past.length;
}
